export class ApiError extends Error {
  readonly name = 'ApiError';
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `a1ec request failed: ${status}`);
  }
}

export class UnauthorizedRedirectError extends Error {
  readonly name = 'UnauthorizedRedirectError';
  constructor() {
    super('Redirecting to Okta login');
  }
}

/**
 * Build the Okta login URL with the current location captured as `returnTo`.
 * Shared by the `apiFetch` 401 path and the SSE wrapper's auth-escalation path
 * (`sse.ts`) so the redirect shape stays in one place.
 */
export function loginRedirectUrl(): string {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  return `/api/v1/auth/okta/login?returnTo=${returnTo}`;
}

/**
 * Shared 401-redirect / non-2xx-throw contract for `/api/*` callers.
 *
 * On 401, navigates to the Okta login URL and throws `UnauthorizedRedirectError`
 * (terminal — the page is leaving). On other non-2xx, throws `ApiError` with the
 * parsed JSON body (when Content-Type matches) or text. On 2xx, returns without
 * touching the body so the caller can read it however it likes.
 */
export async function assertOkOrThrow(response: Response): Promise<void> {
  if (response.status === 401) {
    window.location.assign(loginRedirectUrl());
    throw new UnauthorizedRedirectError();
  }
  if (!response.ok) {
    const body = await readBody(response);
    throw new ApiError(response.status, body);
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });

  await assertOkOrThrow(response);

  // A CloudFront SPA error-rewrite (403/404 → 200 index.html) returns HTML on a
  // 2xx. Guard the content-type before response.json() so we throw a typed
  // ApiError instead of an opaque SyntaxError from the JSON parse. See A1EE-323.
  const ct = response.headers.get('Content-Type') ?? '';
  if (!ct.includes('application/json')) {
    const body = await response.text();
    throw new ApiError(
      response.status,
      body,
      `a1ec returned non-JSON on 2xx: "${ct || 'no content-type'}"`,
    );
  }
  return (await response.json()) as T;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return text;
  const ct = response.headers.get('Content-Type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}
